import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type React from 'react';
import { authFetch } from '@/lib/http/client';
import {
  TRANSCRIPT_PLAYHEAD_EVENT,
  TRANSCRIPT_SEEK_EVENT,
  type TranscriptSeekEventDetail,
} from '@/lib/recordings/transcript-events';

// ── Constants ────────────────────────────────────────────────────────────────

export const SPEED_CYCLE = [1, 1.5, 2] as const;
export type Speed = (typeof SPEED_CYCLE)[number];

const PURGED_STATUS_CODES = new Set([404, 410]);
/** Refetch the presigned URL if it's older than this (server expiry 5min, 60s buffer). */
const REFETCH_AFTER_MS = 4 * 60 * 1000;
/** Cap on consecutive auto-refetch attempts from `<audio>` error events. */
const MAX_AUDIO_RETRY_ATTEMPTS = 3;
/** Throttle timeupdate-driven setState + playhead dispatch. */
const TIMEUPDATE_THROTTLE_MS = 200;

// ── URL fetch state ──────────────────────────────────────────────────────────

type PlaybackUrlResponse = { url: string; expiresInSeconds: number };

function isPlaybackUrlResponse(x: unknown): x is PlaybackUrlResponse {
  if (typeof x !== 'object' || x === null) return false;
  const v = x as Record<string, unknown>;
  return typeof v.url === 'string' && typeof v.expiresInSeconds === 'number';
}

export type PlaybackFetchState =
  | { kind: 'loading' }
  | { kind: 'ready'; url: string; fetchedAt: number }
  | { kind: 'purged' }
  | { kind: 'error'; message: string };

/**
 * Resolves a recording's audio playback URL into the response the hook inspects.
 * The default hits the authenticated `/recordings/:id/playback-url` route; the
 * public share path injects a fetcher that hits `/shared/:token/audio` instead
 * (it has a share token, not a bearer token). Returns the raw Response so the
 * hook can read `.ok`/`.status` for purge (404/410) detection.
 */
export type PlaybackUrlFetcher = (signal: AbortSignal) => Promise<Response>;

function usePlaybackUrl(recordingId: string, fetchPlaybackUrl?: PlaybackUrlFetcher) {
  const [state, setState] = useState<PlaybackFetchState>({ kind: 'loading' });
  const recordingIdRef = useRef(recordingId);
  recordingIdRef.current = recordingId;
  // Hold the (possibly per-render) fetcher in a ref so fetchUrl stays stable and
  // always calls the latest one without re-running the mount effect.
  const fetcherRef = useRef(fetchPlaybackUrl);
  fetcherRef.current = fetchPlaybackUrl;
  const mountedRef = useRef(true);
  const inFlightRef = useRef<AbortController | null>(null);

  const safeSetState = useCallback((next: PlaybackFetchState) => {
    if (!mountedRef.current) return;
    setState(next);
  }, []);

  const fetchUrl = useCallback(async () => {
    inFlightRef.current?.abort();
    const controller = new AbortController();
    inFlightRef.current = controller;
    safeSetState({ kind: 'loading' });
    try {
      const response = fetcherRef.current
        ? await fetcherRef.current(controller.signal)
        : await authFetch(`/recordings/${recordingIdRef.current}/playback-url`, {
            signal: controller.signal,
          });
      if (controller.signal.aborted) return;
      if (!response.ok) {
        if (PURGED_STATUS_CODES.has(response.status)) {
          safeSetState({ kind: 'purged' });
          return;
        }
        safeSetState({ kind: 'error', message: `playback-url ${response.status}` });
        return;
      }
      const raw = await response.json();
      if (controller.signal.aborted) return;
      if (!isPlaybackUrlResponse(raw)) {
        throw new Error(`playback-url response missing 'url' field (got ${JSON.stringify(raw)})`);
      }
      safeSetState({ kind: 'ready', url: raw.url, fetchedAt: Date.now() });
    } catch (err) {
      if (controller.signal.aborted) return;
      if (err instanceof DOMException && err.name === 'AbortError') return;
      safeSetState({ kind: 'error', message: err instanceof Error ? err.message : String(err) });
    }
  }, [safeSetState]);

  useEffect(() => {
    mountedRef.current = true;
    void fetchUrl();
    return () => {
      mountedRef.current = false;
      inFlightRef.current?.abort();
    };
  }, [recordingId, fetchUrl]);

  return { state, refetch: fetchUrl };
}

// ── Public hook ──────────────────────────────────────────────────────────────

export interface UseAudioPlaybackResult {
  state: PlaybackFetchState;
  isPlaying: boolean;
  currentTimeMs: number;
  totalMs: number;
  speed: Speed;
  audioErrorCapped: boolean;
  togglePlay: () => Promise<void>;
  cycleSpeed: () => void;
  skip: (deltaSec: number) => void;
  seekToMs: (ms: number) => void;
  retry: () => void;
  /** The <audio> element — render it somewhere in the consumer's tree. */
  audioElement: ReactNode;
}

/**
 * Audio playback engine for a completed recording. Owns the presigned-URL
 * fetch (with stale-URL refetch + retry cap), the `<audio>` element, play/pause/
 * seek/speed state, and the transcript SEEK→seek / timeupdate→PLAYHEAD event
 * bridge. Consumers render `audioElement` and drive the controls; the visual
 * layout is entirely theirs (AudioMiniPlayer pill, TranscriptPlayer squircle).
 */
export interface UseAudioPlaybackOptions {
  /** Override how the playback URL is fetched (the share path hits the
   * token-scoped `/shared/:token/audio` instead of the authenticated route). */
  fetchPlaybackUrl?: PlaybackUrlFetcher;
}

export function useAudioPlayback(
  recordingId: string,
  durationMs?: number,
  options?: UseAudioPlaybackOptions,
): UseAudioPlaybackResult {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const { state, refetch } = usePlaybackUrl(recordingId, options?.fetchPlaybackUrl);

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [audioDurationMs, setAudioDurationMs] = useState<number | null>(durationMs ?? null);
  const [speedIdx, setSpeedIdx] = useState(0);
  const audioRetryAttemptsRef = useRef(0);
  const [audioErrorCapped, setAudioErrorCapped] = useState(false);
  const pendingSeekMsRef = useRef<number | null>(null);
  const lastTimeUpdateAtRef = useRef(0);
  const pendingDeferredPlayRef = useRef(false);

  const speed: Speed = SPEED_CYCLE[speedIdx]!;
  const totalMs = audioDurationMs ?? durationMs ?? 0;

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.playbackRate = speed;
  }, [speed]);

  const dispatchPlayheadCleared = useCallback(() => {
    window.dispatchEvent(
      new CustomEvent(TRANSCRIPT_PLAYHEAD_EVENT, { detail: { recordingId, currentTimeMs: null } }),
    );
  }, [recordingId]);

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => {
    setIsPlaying(false);
    dispatchPlayheadCleared();
  };
  const handleEnded = () => {
    setIsPlaying(false);
    dispatchPlayheadCleared();
  };
  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const now = Date.now();
    if (now - lastTimeUpdateAtRef.current < TIMEUPDATE_THROTTLE_MS) return;
    lastTimeUpdateAtRef.current = now;
    const ms = Math.floor(e.currentTarget.currentTime * 1000);
    setCurrentTimeMs(ms);
    window.dispatchEvent(
      new CustomEvent(TRANSCRIPT_PLAYHEAD_EVENT, { detail: { recordingId, currentTimeMs: ms } }),
    );
  };
  const handleDurationChange = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const d = e.currentTarget.duration;
    if (Number.isFinite(d) && d > 0) setAudioDurationMs(Math.floor(d * 1000));
  };
  const handleError = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const code = e.currentTarget.error?.code;
    if (code === 2 || code === 4) {
      if (audioRetryAttemptsRef.current >= MAX_AUDIO_RETRY_ATTEMPTS) {
        setAudioErrorCapped(true);
        return;
      }
      audioRetryAttemptsRef.current += 1;
      void refetch();
    }
  };
  const handleLoadedData = () => {
    audioRetryAttemptsRef.current = 0;
    if (audioErrorCapped) setAudioErrorCapped(false);
  };
  const handleLoadedMetadata = (e: React.SyntheticEvent<HTMLAudioElement>) => {
    const el = e.currentTarget;
    el.playbackRate = speed;
    const queued = pendingSeekMsRef.current;
    if (queued !== null) {
      pendingSeekMsRef.current = null;
      el.currentTime = queued / 1000;
      setCurrentTimeMs(queued);
      const result = el.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    }
  };

  const togglePlay = useCallback(async () => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      pendingDeferredPlayRef.current = false;
      el.pause();
      return;
    }
    if (state.kind === 'ready') {
      const age = Date.now() - state.fetchedAt;
      if (age > REFETCH_AFTER_MS) {
        await refetch();
        pendingDeferredPlayRef.current = true;
        requestAnimationFrame(() => {
          if (!pendingDeferredPlayRef.current) return;
          pendingDeferredPlayRef.current = false;
          const result = audioRef.current?.play();
          if (result && typeof result.catch === 'function') result.catch(() => {});
        });
        return;
      }
    }
    try {
      const result = el.play();
      if (result && typeof result.then === 'function') await result;
    } catch {
      /* autoplay rejection — user can retry */
    }
  }, [isPlaying, state, refetch]);

  const seekToMs = useCallback((ms: number) => {
    const el = audioRef.current;
    if (!el) {
      pendingSeekMsRef.current = ms;
      return;
    }
    const upper = Number.isFinite(el.duration) && el.duration > 0 ? el.duration * 1000 : totalMs;
    const clamped = upper > 0 ? Math.max(0, Math.min(upper, ms)) : Math.max(0, ms);
    el.currentTime = clamped / 1000;
    setCurrentTimeMs(Math.floor(clamped));
  }, [totalMs]);

  const skip = useCallback((deltaSec: number) => {
    const el = audioRef.current;
    if (!el) return;
    const elDuration = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : null;
    const fallback = totalMs > 0 ? totalMs / 1000 : null;
    const upper = elDuration ?? fallback;
    if (upper === null) return;
    const next = Math.max(0, Math.min(upper, el.currentTime + deltaSec));
    el.currentTime = next;
    setCurrentTimeMs(Math.floor(next * 1000));
  }, [totalMs]);

  const cycleSpeed = useCallback(() => setSpeedIdx((i) => (i + 1) % SPEED_CYCLE.length), []);

  const retry = useCallback(() => {
    audioRetryAttemptsRef.current = 0;
    setAudioErrorCapped(false);
    void refetch();
  }, [refetch]);

  // Listen for transcript-row seek events targeting this recording.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<TranscriptSeekEventDetail>).detail;
      if (!detail || detail.recordingId !== recordingId) return;
      const el = audioRef.current;
      if (!el) {
        pendingSeekMsRef.current = detail.startMs;
        return;
      }
      el.currentTime = detail.startMs / 1000;
      setCurrentTimeMs(detail.startMs);
      const result = el.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    };
    window.addEventListener(TRANSCRIPT_SEEK_EVENT, handler);
    return () => window.removeEventListener(TRANSCRIPT_SEEK_EVENT, handler);
  }, [recordingId]);

  const audioElement = useMemo(
    () =>
      state.kind === 'ready' ? (
        <audio
          ref={audioRef}
          src={state.url}
          preload="metadata"
          hidden
          onPlay={handlePlay}
          onPause={handlePause}
          onEnded={handleEnded}
          onTimeUpdate={handleTimeUpdate}
          onDurationChange={handleDurationChange}
          onLoadedData={handleLoadedData}
          onLoadedMetadata={handleLoadedMetadata}
          onError={handleError}
        />
      ) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers are stable enough for the element identity; src drives reload
    [state],
  );

  return {
    state,
    isPlaying,
    currentTimeMs,
    totalMs,
    speed,
    audioErrorCapped,
    togglePlay,
    cycleSpeed,
    skip,
    seekToMs,
    retry,
    audioElement,
  };
}
